import { useState } from 'preact/hooks';
import type { PrActionBody, PrDetailBody } from '../../shared/pr/api';
import { useI18n } from '../i18n';
import { ApiError, apiSend } from '../lib/api';

// The department head's decision (P1.5, phone and desktop). Shown only when the broker offers the
// head actions; any decision returns the request to Procurement.

type HeadAction = 'headApprove' | 'headReturn' | 'headReject';

export function HeadDecision({ pr, onDone }: { pr: PrDetailBody; onDone: () => void }) {
  const { t } = useI18n();
  const [remark, setRemark] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function act(action: HeadAction) {
    if (action !== 'headApprove' && !remark.trim()) {
      setProblem(t('review.error.reason_required'));
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      await apiSend<PrActionBody>('POST', `/api/prs/${encodeURIComponent(pr.prNo)}/action`, {
        action,
        etag: pr.etag,
        reason: remark,
      });
      onDone();
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      const known = [
        'self_approval',
        'reason_required',
        'invalid_transition',
        'conflict',
        'forbidden',
      ];
      setProblem(
        t(
          known.includes(e?.code ?? '')
            ? (`review.error.${e?.code ?? ''}` as 'review.error.generic')
            : 'review.error.generic',
        ) + (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="card card--emphasis stack" aria-labelledby="head-title">
      <h3 id="head-title" class="card__title">
        {t('head.title')}
      </h3>
      {pr.deptHeadReason && (
        <p class="notice notice--info">
          <strong>{t('head.asks')}</strong> <span dir="auto">{pr.deptHeadReason}</span>
        </p>
      )}
      <p class="caption">{t('head.hint')}</p>
      {problem && (
        <p class="form-problem" role="alert">
          {problem}
        </p>
      )}
      <div class="field">
        <label for="head-remark">{t('head.remark')}</label>
        <textarea
          id="head-remark"
          rows={3}
          maxLength={2000}
          value={remark}
          onInput={(e) => setRemark((e.target as HTMLTextAreaElement).value)}
        />
      </div>
      <div class="actions head-actions">
        {pr.actions.includes('headReject') && (
          <button
            type="button"
            class="button button--danger"
            disabled={busy}
            onClick={() => void act('headReject')}
          >
            {t('head.reject')}
          </button>
        )}
        {pr.actions.includes('headReturn') && (
          <button
            type="button"
            class="button"
            disabled={busy}
            onClick={() => void act('headReturn')}
          >
            {t('head.return')}
          </button>
        )}
        {pr.actions.includes('headApprove') && (
          <button
            type="button"
            class="button button--primary"
            disabled={busy}
            onClick={() => void act('headApprove')}
          >
            {t('head.approve')}
          </button>
        )}
      </div>
    </section>
  );
}
